import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Sparkles } from 'lucide-react'

import { DashboardLayout } from '@/components/layout'
import { PageHeader } from '@/lib/atsUi'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, clients } from '@/lib/mocks'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/jobs/new')({ component: NewJob })

function NewJob() {
  const navigate = useNavigate()

  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState('')
  const [location, setLocation] = useState('')
  const [ctcMin, setCtcMin] = useState('')
  const [ctcMax, setCtcMax] = useState('')
  const [skills, setSkills] = useState('')
  const [openings, setOpenings] = useState('')
  const [feePct, setFeePct] = useState('')
  const [guaranteeDays, setGuaranteeDays] = useState('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    toast.success('Job created — AI is drafting the JD and sourcing')
    navigate({ to: '/jobs' })
  }

  return (
    <DashboardLayout title="New job" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="New job"
        subtitle="Tell us the role — the AI drafts the JD and starts sourcing."
        action={
          <Button size="sm" variant="outline" asChild>
            <Link to="/jobs">Cancel</Link>
          </Button>
        }
      />

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent className="space-y-6 pt-6">
              {/* Job title */}
              <div className="space-y-1.5">
                <Label htmlFor="title">Job title</Label>
                <Input
                  id="title"
                  placeholder="e.g. Senior Backend Engineer"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {/* Client */}
              <div className="space-y-1.5">
                <Label htmlFor="client">Client</Label>
                <select
                  id="client"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select a client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div className="space-y-1.5">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g. Bengaluru / Remote"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>

              {/* CTC range */}
              <div className="space-y-1.5">
                <Label>CTC range (LPA)</Label>
                <div className="flex gap-3">
                  <Input
                    placeholder="Min"
                    type="number"
                    value={ctcMin}
                    onChange={(e) => setCtcMin(e.target.value)}
                  />
                  <Input
                    placeholder="Max"
                    type="number"
                    value={ctcMax}
                    onChange={(e) => setCtcMax(e.target.value)}
                  />
                </div>
              </div>

              {/* Key skills */}
              <div className="space-y-1.5">
                <Label htmlFor="skills">Key skills</Label>
                <Input
                  id="skills"
                  placeholder="e.g. Node.js, PostgreSQL, Docker"
                  hint="Comma separated"
                  value={skills}
                  onChange={(e) => setSkills(e.target.value)}
                />
              </div>

              {/* Openings */}
              <div className="space-y-1.5">
                <Label htmlFor="openings">Openings</Label>
                <Input
                  id="openings"
                  type="number"
                  placeholder="1"
                  value={openings}
                  onChange={(e) => setOpenings(e.target.value)}
                />
              </div>

              {/* Fee % */}
              <div className="space-y-1.5">
                <Label htmlFor="feePct">Fee %</Label>
                <Input
                  id="feePct"
                  type="number"
                  placeholder="e.g. 8.33"
                  value={feePct}
                  onChange={(e) => setFeePct(e.target.value)}
                />
              </div>

              {/* Guarantee days */}
              <div className="space-y-1.5">
                <Label htmlFor="guaranteeDays">Guarantee days</Label>
                <Input
                  id="guaranteeDays"
                  type="number"
                  placeholder="e.g. 90"
                  value={guaranteeDays}
                  onChange={(e) => setGuaranteeDays(e.target.value)}
                />
              </div>
            </CardContent>

            <CardFooter className="flex items-center gap-4 border-t pt-5">
              <Button type="submit">Create &amp; start sourcing</Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/jobs">Cancel</Link>
              </Button>
            </CardFooter>
          </Card>
        </form>

        <p className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
          Once created, the AI will generate a boosted JD, publish to Naukri + LinkedIn, and begin
          sourcing — you&apos;ll approve the shortlist.
        </p>
      </div>
    </DashboardLayout>
  )
}
